/**
 * Annualised capital £/yr/veh for Fleet Cost Assumptions, by acquisition method:
 * - purchase: (purchase + fees − residual% of purchase) ÷ useful life
 *   (conversion costs are excluded)
 * - lease: monthly × 12 + (upfront + end-of-term fees) spread over the term
 * - rental: rate × periods per year (charged every calendar day)
 *
 * Keep in sync with zeecore fleetCostCapitalAnnualGbp (FleetCostConfigService.ts).
 */

import type {
  FleetCostAcquisitionMethod,
  FleetCostAcquisitionMix,
  FleetCostCapitalInputs,
  FleetCostRentalPeriod,
} from '../types/fleetCostAssumptions';

export type CapitalAnnualiseInput = FleetCostCapitalInputs;

/** Same 365-day basis as Standing £/yr → £/day; week = 365 ÷ 7. */
export const RENTAL_PERIODS_PER_YEAR: Record<FleetCostRentalPeriod, number> = {
  day: 365,
  week: 365 / 7,
  month: 12,
};

export const ACQUISITION_METHOD_LABELS: Record<FleetCostAcquisitionMethod, string> = {
  purchase: 'Purchase',
  lease: 'Lease',
  rental: 'Rental',
};

export const RENTAL_PERIOD_LABELS: Record<FleetCostRentalPeriod, string> = {
  day: 'per day',
  week: 'per week',
  month: 'per month',
};

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function acquisitionMethodOf(
  v: Pick<FleetCostCapitalInputs, 'acquisitionMethod'>,
): FleetCostAcquisitionMethod {
  return v.acquisitionMethod === 'lease' || v.acquisitionMethod === 'rental'
    ? v.acquisitionMethod
    : 'purchase';
}

export function rentalPeriodOf(v: Pick<FleetCostCapitalInputs, 'rentalPeriod'>): FleetCostRentalPeriod {
  return v.rentalPeriod === 'day' || v.rentalPeriod === 'month' ? v.rentalPeriod : 'week';
}

/** Capital £/yr for one vehicle on the row's acquisitionMethod (default purchase). */
export function capitalAnnualisedGbp(v: CapitalAnnualiseInput): number {
  return capitalAnnualForMethodGbp(v, acquisitionMethodOf(v));
}

/** Capital £/yr for one vehicle acquired by `method`, from that method's inputs. */
export function capitalAnnualForMethodGbp(
  v: CapitalAnnualiseInput,
  method: FleetCostAcquisitionMethod,
): number {
  if (method === 'lease') {
    const monthly = Math.max(0, finiteOr(v.leaseMonthly, 0));
    const termYears = Math.max(1, finiteOr(v.leaseTermMonths, 12)) / 12;
    const oneOff = Math.max(0, finiteOr(v.leaseUpfront, 0)) + Math.max(0, finiteOr(v.leaseEndFees, 0));
    return monthly * 12 + oneOff / termYears;
  }
  if (method === 'rental') {
    return Math.max(0, finiteOr(v.rentalRate, 0)) * RENTAL_PERIODS_PER_YEAR[rentalPeriodOf(v)];
  }
  const life = Math.max(1, finiteOr(v.serviceLife, 1) || 1);
  return capitalDepreciableGbp(v) / life;
}

export function capitalDepreciableGbp(v: CapitalAnnualiseInput): number {
  const purchase = finiteOr(v.unitCost, 0);
  const fees = finiteOr(v.initialTaxesFees, 2000);
  const residualPct = capitalResidualPct(v);
  return Math.max(0, purchase + fees - purchase * (residualPct / 100));
}

export function capitalResidualPct(v: CapitalAnnualiseInput): number {
  return Math.min(100, Math.max(0, finiteOr(v.residualValue, 15)));
}

/** Canonical numbers for app↔zeecore contract tests (trailerStandingContract.test.ts). */
export const CAPITAL_CONTRACT = {
  /** £500/mo × 12 + (£1500 upfront + £500 end) ÷ 5 yr = £6400/yr. */
  lease: {
    input: { acquisitionMethod: 'lease', leaseMonthly: 500, leaseTermMonths: 60, leaseUpfront: 1500, leaseEndFees: 500 },
    annual: 6400,
  },
  /** £100/week × 365 ÷ 7. */
  rental: {
    input: { acquisitionMethod: 'rental', rentalRate: 100, rentalPeriod: 'week' },
    annual: (100 * 365) / 7,
  },
  /**
   * 4 bought (£100k + £2k − 15%, 10 yr → £8.7k), 6 leased (£1k/mo → £12k):
   * (4 × 8700 + 6 × 12000) ÷ 10 = £10,680/yr.
   */
  mix: {
    input: {
      purchaseQty: 4, unitCost: 100_000, initialTaxesFees: 2_000, residualValue: 15, serviceLife: 10,
      leaseQty: 6, leaseMonthly: 1_000, leaseTermMonths: 36,
    },
    annual: 10_680,
  },
} as const;

export const ACQUISITION_METHODS: readonly FleetCostAcquisitionMethod[] = ['purchase', 'lease', 'rental'];

const MIX_FIELD: Record<FleetCostAcquisitionMethod, keyof FleetCostAcquisitionMix> = {
  purchase: 'purchaseQty',
  lease: 'leaseQty',
  rental: 'rentalQty',
};

export function acquisitionMixField(method: FleetCostAcquisitionMethod): keyof FleetCostAcquisitionMix {
  return MIX_FIELD[method];
}

/**
 * Vehicles per method for a class. With no counts entered, the whole class is
 * on its acquisitionMethod (weight 1) so older configs keep their figures.
 */
export function acquisitionMixCounts(
  v: CapitalAnnualiseInput & FleetCostAcquisitionMix,
): {
  /** Weights per method; purchase includes `unset` (assumed purchased). */
  counts: Record<FleetCostAcquisitionMethod, number>;
  total: number;
  entered: boolean;
  /** Vehicles with no Acquisition set (already inside counts.purchase). */
  unset: number;
} {
  const counts = {} as Record<FleetCostAcquisitionMethod, number>;
  for (const m of ACQUISITION_METHODS) counts[m] = Math.max(0, finiteOr(v[MIX_FIELD[m]], 0));
  const unset = Math.max(0, finiteOr(v.unsetQty, 0));
  counts.purchase += unset;
  const total = ACQUISITION_METHODS.reduce((sum, m) => sum + counts[m], 0);
  if (total > 0) return { counts, total, entered: true, unset };
  const fallback = { purchase: 0, lease: 0, rental: 0 };
  fallback[acquisitionMethodOf(v)] = 1;
  return { counts: fallback, total: 1, entered: false, unset: 0 };
}

/** Methods a class uses: those with a count above 0 (or its single method). */
export function acquisitionMethodsInUse(
  v: CapitalAnnualiseInput & FleetCostAcquisitionMix,
): FleetCostAcquisitionMethod[] {
  const { counts } = acquisitionMixCounts(v);
  return ACQUISITION_METHODS.filter((m) => counts[m] > 0);
}

/**
 * Class capital £/yr per vehicle: count-weighted average of purchase, lease
 * and rental. Keep in sync with zeecore fleetCostVehicleCapitalAnnualGbp.
 */
export function vehicleCapitalAnnualisedGbp(v: CapitalAnnualiseInput & FleetCostAcquisitionMix): number {
  const { counts, total } = acquisitionMixCounts(v);
  let sum = 0;
  for (const m of ACQUISITION_METHODS) {
    if (counts[m] > 0) sum += capitalAnnualForMethodGbp(v, m) * counts[m];
  }
  return sum / total;
}

/**
 * Class row with its vehicle counts taken from Assets Acquisition. Vehicles
 * with no Acquisition set go to unsetQty (weighted as purchase in the
 * capital total, but stored apart). No Assets vehicles in the class clears
 * the counts, so the class falls back to fully purchased.
 */
export function withAcquisitionMixFromAssets<T extends FleetCostAcquisitionMix>(
  row: T,
  mix: { purchase: number; lease: number; rental: number; unset: number } | undefined,
): T {
  if (!mix) return { ...row, purchaseQty: 0, leaseQty: 0, rentalQty: 0, unsetQty: 0 };
  return {
    ...row,
    purchaseQty: mix.purchase,
    leaseQty: mix.lease,
    rentalQty: mix.rental,
    unsetQty: mix.unset,
  };
}
